import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Snackbar, Alert, Button, Menu, MenuItem } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Task, TaskStatus } from '@/types';
import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { useAppDispatch, useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { openModal } from '@/store/slices/uiSlice';
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import {
  TaskTable,
  useTaskTableActions,
  MY_TASKS_CONFIG,
  canDelete,
  canEdit,
  BulkTaskActionsBar,
  getDeleteTaskMessage,
} from '@/shared/components/TaskTable';

interface TaskListViewProps {
  tasks: Task[];
  isLoading: boolean;
  currentRaciRole?: 'responsible' | 'accountable' | 'consulted' | 'informed';
  allTasksForValidation?: Task[];
}

/**
 * TaskListView
 * Table-based task list with quick actions (desktop)
 * Card-based task list for mobile
 * Supports multi-select + bulk delete
 */
const TaskListView: React.FC<TaskListViewProps> = ({
  tasks,
  isLoading,
  currentRaciRole,
  allTasksForValidation,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const user = useAppSelector(selectCurrentUser);
  const { showError, showSuccess } = useSnackbar();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTask, setMenuTask] = useState<Task | null>(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const {
    handleCheckboxChange,
    confirmDelete,
    handleComplete,
    confirmCompleteTask,
    confirmDeleteTask,
    blockingWarning,
    blockingTask,
    setConfirmCompleteTask,
    setConfirmDeleteTask,
    clearBlockingWarning,
  } = useTaskTableActions({
    context: 'myTasks',
    allTasks: allTasksForValidation ?? tasks,
  });

  // Drop selections that are no longer on the current page / list
  useEffect(() => {
    const visibleIds = new Set(tasks.map((t) => t.id));
    setSelectedTaskIds((prev) => {
      const next = new Set(Array.from(prev).filter((id) => visibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [tasks]);

  const selectedTasks = useMemo(
    () => tasks.filter((t) => selectedTaskIds.has(t.id)),
    [tasks, selectedTaskIds]
  );

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map((id) => taskService.deleteTask(id)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      const succeeded = results.length - failed;
      return { succeeded, failed };
    },
    onSuccess: ({ succeeded, failed }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myTasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });
      setSelectedTaskIds(new Set());
      setConfirmBulkDelete(false);
      if (failed > 0) {
        showError(`Deleted ${succeeded} task(s); ${failed} failed`);
      } else {
        showSuccess(`Deleted ${succeeded} task${succeeded === 1 ? '' : 's'}`);
      }
    },
    onError: (error) => {
      showError(error instanceof Error ? error.message : 'Failed to delete tasks');
      setConfirmBulkDelete(false);
    },
  });

  const handleTaskClick = (taskId: string) => {
    navigate(`/tasks?taskId=${taskId}`);
  };

  const handleViewBlocker = () => {
    if (blockingTask) {
      handleTaskClick(blockingTask.id);
      clearBlockingWarning();
    }
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuTask(null);
  };

  const handleMenuView = () => {
    if (menuTask) navigate(`/tasks?taskId=${menuTask.id}`);
    handleMenuClose();
  };

  const handleMenuEdit = () => {
    if (menuTask) {
      dispatch(openModal({ type: 'taskEdit', data: menuTask as unknown as Record<string, unknown> }));
    }
    handleMenuClose();
  };

  const handleMenuDelete = () => {
    if (menuTask) setConfirmDeleteTask(menuTask);
    handleMenuClose();
  };

  const handleSelectionChange = (taskId: string, selected: boolean) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(taskId);
      else next.delete(taskId);
      return next;
    });
  };

  const handleSelectAllChange = (selected: boolean) => {
    if (!selected) {
      setSelectedTaskIds(new Set());
      return;
    }
    const deletableIds = tasks
      .filter((task) => user && canDelete(task, user))
      .map((task) => task.id);
    setSelectedTaskIds(new Set(deletableIds));
  };

  return (
    <>
      <TaskTable
        tasks={tasks}
        isLoading={isLoading}
        context="myTasks"
        config={MY_TASKS_CONFIG}
        currentRaciRole={currentRaciRole}
        selectedTaskIds={selectedTaskIds}
        onTaskClick={handleTaskClick}
        onCheckboxChange={handleCheckboxChange}
        onSelectionChange={handleSelectionChange}
        onSelectAllChange={handleSelectAllChange}
        onMenuAction={(action, task, event) => {
          if (action === 'menu' && event) {
            setMenuAnchor(event.currentTarget);
            setMenuTask(task);
          }
        }}
      />

      <BulkTaskActionsBar
        selectedCount={selectedTaskIds.size}
        isDeleting={bulkDeleteMutation.isPending}
        onClear={() => setSelectedTaskIds(new Set())}
        onDelete={() => setConfirmBulkDelete(true)}
      />

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 160 } }}
      >
        <MenuItem onClick={handleMenuView}>View details</MenuItem>
        {menuTask &&
          user &&
          canEdit(menuTask, user) &&
          menuTask.status !== TaskStatus.COMPLETED &&
          menuTask.status !== TaskStatus.CANCELLED && (
            <MenuItem onClick={handleMenuEdit}>Edit task</MenuItem>
          )}
        {menuTask && user && canDelete(menuTask, user) && (
          <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>
            Delete
          </MenuItem>
        )}
      </Menu>

      {confirmCompleteTask && (
        <ConfirmationModal
          open={true}
          onClose={() => setConfirmCompleteTask(null)}
          data={{
            title: 'Mark as completed',
            message: `Are you sure you want to mark "${confirmCompleteTask.title}" as completed?`,
            type: 'success',
            confirmText: 'Complete',
            onConfirm: () => handleComplete(confirmCompleteTask),
          }}
        />
      )}

      {confirmDeleteTask && (
        <ConfirmationModal
          open={true}
          onClose={() => setConfirmDeleteTask(null)}
          data={{
            title: 'Delete task',
            message: getDeleteTaskMessage(confirmDeleteTask),
            type: 'danger',
            confirmText: 'Delete',
            onConfirm: () => confirmDelete(confirmDeleteTask),
          }}
        />
      )}

      {confirmBulkDelete && (
        <ConfirmationModal
          open={true}
          onClose={() => setConfirmBulkDelete(false)}
          data={{
            title: 'Delete selected tasks',
            message: `Delete ${selectedTasks.length} selected task${
              selectedTasks.length === 1 ? '' : 's'
            }? This cannot be undone.`,
            type: 'danger',
            confirmText: `Delete ${selectedTasks.length}`,
            onConfirm: () =>
              bulkDeleteMutation.mutate(selectedTasks.map((task) => task.id)),
          }}
        />
      )}

      <Snackbar
        open={!!blockingWarning}
        autoHideDuration={6000}
        onClose={clearBlockingWarning}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 80, md: 24 } }}
      >
        <Alert
          onClose={clearBlockingWarning}
          severity="warning"
          sx={{ width: '100%' }}
          action={
            blockingTask && (
              <Button color="inherit" size="small" onClick={handleViewBlocker}>
                View Blocker
              </Button>
            )
          }
        >
          {blockingWarning}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TaskListView;
